import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js';
import { FiActivity, FiAlertOctagon, FiAlertTriangle, FiDatabase, FiRefreshCw } from 'react-icons/fi';
import { Badge, Card, DataTable, Drawer, EmptyState, IconButton, PageHeader, Skeleton, StatTile } from '../components/ui';
import {
  AI_CIRCUIT_LABELS,
  AI_COLOR_BADGES,
  AI_RECONCILIATION_REASONS,
  AI_SPEND_MIGRATION_MESSAGE,
  aiCircuitLabel,
  aiMonthName,
  aiUserName,
  formatAiBudget,
  formatGapPercent,
  formatPercent,
  formatUsd,
  getAiSpend,
  getAiSpendDetail,
  isAiSpendMigrationMissing,
} from '../servicesV2/aiSpendService';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const formatDay = (day) => {
  const date = new Date(`${String(day).slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? String(day)
    : date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
};

function ColorBadge({ color }) {
  const entry = AI_COLOR_BADGES[color];
  if (!entry) return <span className="text-fg-subtle">—</span>;
  return (
    <Badge variant={entry.variant} dot>
      {entry.label}
    </Badge>
  );
}

// Status of one day of the Azure check: ok / gap (past 10 %, or nothing billed) / error (reason in French, muted).
// A day with unread clients is never « Conforme »: its journal total is incomplete.
function ReconciliationStatus({ entry }) {
  if (entry.status === 'gap') return <Badge variant="danger" dot>Écart</Badge>;
  if (entry.status === 'ok' && entry.failedTenants > 0) return <Badge variant="warning" dot>Journal incomplet</Badge>;
  if (entry.status === 'ok') return <Badge variant="success" dot>Conforme</Badge>;
  return (
    <span className="text-caption text-fg-muted">
      {AI_RECONCILIATION_REASONS[entry.reason] || 'Recoupement impossible'}
    </span>
  );
}

// « Recoupement avec la facture Azure »: one line per day, journal vs. Azure bill (calculated at J-2).
function ReconciliationCard({ days }) {
  return (
    <Card padding="none" className="mt-5">
      <div className="border-b border-line px-4 py-3">
        <h3 className="text-body-sm font-semibold text-fg">Recoupement avec la facture Azure</h3>
      </div>
      {days.length === 0 ? (
        <p className="px-4 py-3 text-caption text-fg-muted">Aucun recoupement encore calculé.</p>
      ) : (
        <ul className="divide-y divide-line">
          {days.map((entry) => (
            <li key={entry.day} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2 text-body-sm">
              <span className="w-12 tabular-nums text-fg">{formatDay(entry.day)}</span>
              <span className="text-fg-muted">
                Azure <span className="tabular-nums text-fg">{formatUsd(entry.azureUsd)}</span>
              </span>
              <span className="text-fg-muted">
                Journal <span className="tabular-nums text-fg">{formatUsd(entry.journalAzureUsd)}</span>
              </span>
              <span className="w-24 text-right tabular-nums text-fg-muted">{formatGapPercent(entry.gapPercent)}</span>
              <ReconciliationStatus entry={entry} />
              <span className="text-caption text-fg-muted">
                autres fournisseurs <span className="tabular-nums">{formatUsd(entry.journalOtherUsd)}</span>
              </span>
              {entry.failedTenants > 0 && (
                <span className="text-caption text-fg-muted">
                  {entry.failedTenants} {entry.failedTenants > 1 ? 'clients non lus' : 'client non lu'}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// Circuits with spend, biggest first: « Chat 12,30 $ · Processus 3,10 $ ».
function CircuitBreakdown({ byCircuit }) {
  const parts = Object.keys(AI_CIRCUIT_LABELS)
    .map((circuit) => ({ circuit, cost: Number(byCircuit?.[circuit] || 0) }))
    .filter((part) => part.cost > 0)
    .sort((a, b) => b.cost - a.cost);
  if (parts.length === 0) return <span className="text-fg-subtle">—</span>;
  return (
    <p className="text-caption text-fg-muted">
      {parts.map((part, index) => (
        <span key={part.circuit}>
          {index > 0 && ' · '}
          {aiCircuitLabel(part.circuit)} <span className="tabular-nums text-fg">{formatUsd(part.cost)}</span>
        </span>
      ))}
    </p>
  );
}

// Theme colours for Chart.js, re-read when the shell toggles the `dark` class on <html>.
function readChartTheme() {
  const style = getComputedStyle(document.documentElement);
  const rgb = (name, alpha = 1) => `rgba(${style.getPropertyValue(name).trim().split(/\s+/).join(', ')}, ${alpha})`;
  return {
    bar: rgb('--brand', 0.85),
    barHover: rgb('--brand'),
    tick: rgb('--text-muted'),
    grid: rgb('--border'),
    tooltipBg: rgb('--text'),
    tooltipFg: rgb('--text-inverse'),
  };
}

function useChartTheme() {
  const [theme, setTheme] = useState(readChartTheme);
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(readChartTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return theme;
}

function SpendBarChart({ labels, values, horizontal, empty }) {
  const theme = useChartTheme();
  if (values.every((value) => value === 0)) {
    return <p className="px-4 py-3 text-caption text-fg-subtle">{empty}</p>;
  }
  const valueAxis = {
    beginAtZero: true,
    grid: { color: theme.grid },
    border: { display: false },
    ticks: { color: theme.tick, font: { size: 12 }, callback: (value) => formatUsd(value) },
  };
  const labelAxis = {
    grid: { display: false },
    border: { display: false },
    ticks: { color: theme.tick, font: { size: 12 }, autoSkip: !horizontal, maxRotation: 0 },
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: theme.tooltipBg,
        titleColor: theme.tooltipFg,
        bodyColor: theme.tooltipFg,
        displayColors: false,
        callbacks: { label: (ctx) => formatUsd(horizontal ? ctx.parsed.x : ctx.parsed.y) },
      },
    },
    scales: horizontal ? { x: valueAxis, y: labelAxis } : { x: labelAxis, y: valueAxis },
  };
  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: theme.bar,
        hoverBackgroundColor: theme.barHover,
        borderRadius: 4,
        maxBarThickness: horizontal ? 22 : 18,
      },
    ],
  };
  // Horizontal bars grow with the number of rows; the daily chart keeps a fixed height.
  const height = horizontal ? labels.length * 34 + 40 : 220;
  return (
    <div className="px-4 py-3" style={{ height }}>
      <Bar data={data} options={options} />
    </div>
  );
}

// Every day of the month up to today (or to the month end for a past month), 0 when nothing was spent.
function fillMonthDays(month, byDay) {
  const [year, monthIndex] = String(month).split('-').map(Number);
  const daysInMonth = new Date(Date.UTC(year, monthIndex, 0)).getUTCDate();
  const now = new Date();
  const isCurrent = now.getUTCFullYear() === year && now.getUTCMonth() + 1 === monthIndex;
  const lastDay = isCurrent ? now.getUTCDate() : daysInMonth;
  const spent = new Map((byDay || []).map((day) => [String(day.day).slice(0, 10), Number(day.costUsd || 0)]));
  return Array.from({ length: lastDay }, (_, i) => {
    const iso = `${month}-${String(i + 1).padStart(2, '0')}`;
    return { label: formatDay(iso), cost: spent.get(iso) || 0 };
  });
}

function ChartCard({ title, children }) {
  return (
    <Card padding="none">
      <div className="border-b border-line px-4 py-3">
        <h3 className="text-body-sm font-semibold text-fg">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

/** Detail of one client: spend by day, by user (« Système » for user 0), by circuit. */
function ClientDetail({ row, onClose }) {
  const [detail, setDetail] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    if (!row) return undefined;
    let cancelled = false;
    setDetail(null);
    setState('loading');
    getAiSpendDetail(row.key).then(
      (data) => {
        if (cancelled) return;
        setDetail(data);
        setState('ready');
      },
      (error) => !cancelled && setState(isAiSpendMigrationMissing(error) ? 'migration' : 'failed'),
    );
    return () => {
      cancelled = true;
    };
  }, [row]);

  const byCircuit = detail
    ? Object.keys(AI_CIRCUIT_LABELS)
        .map((circuit) => ({ circuit, cost: Number(detail.byCircuit?.[circuit] || 0) }))
        .filter((part) => part.cost > 0)
        .sort((a, b) => b.cost - a.cost)
    : [];
  const byUser = detail
    ? [...(detail.byUser || [])].sort((a, b) => Number(b.costUsd || 0) - Number(a.costUsd || 0))
    : [];
  // `row` is null while the drawer closes, but the last `detail` is still in state.
  const byDay = detail && row ? fillMonthDays(row.month, detail.byDay) : [];

  return (
    <Drawer
      isOpen={!!row}
      close={onClose}
      size="md"
      title={row?.nom}
      subtitle={row ? `${row.key} — ${aiMonthName(row.month)}` : undefined}
    >
      <Drawer.Body className="flex flex-col gap-4">
        {state === 'loading' && (
          <>
            <Skeleton className="h-32 rounded-lg" />
            <Skeleton className="h-32 rounded-lg" />
          </>
        )}
        {state === 'migration' && <p className="text-body-sm text-fg-muted">{AI_SPEND_MIGRATION_MESSAGE}</p>}
        {state === 'failed' && (
          <p className="text-body-sm text-fg-muted">Le détail n'a pas pu être lu. Fermer et rouvrir pour réessayer.</p>
        )}
        {state === 'ready' && detail && (
          <>
            <ChartCard title="Par circuit">
              <SpendBarChart
                horizontal
                labels={byCircuit.map((part) => aiCircuitLabel(part.circuit))}
                values={byCircuit.map((part) => part.cost)}
                empty="Aucune dépense ce mois-ci."
              />
            </ChartCard>
            <ChartCard title="Par utilisateur">
              <SpendBarChart
                horizontal
                labels={byUser.map(aiUserName)}
                values={byUser.map((user) => Number(user.costUsd || 0))}
                empty="Aucune dépense ce mois-ci."
              />
            </ChartCard>
            <ChartCard title="Par jour">
              <SpendBarChart
                labels={byDay.map((day) => day.label)}
                values={byDay.map((day) => day.cost)}
                empty="Aucune dépense ce mois-ci."
              />
            </ChartCard>
          </>
        )}
      </Drawer.Body>
    </Drawer>
  );
}

/**
 * Consommation IA — AI spend of the month per client against its threshold (set in the client
 * sheet). Dollars, calendar month in UTC; green < 80 %, orange >= 80 %, red >= 100 %.
 */
function AiSpendDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState(null); // null | 'migration' | 'failed'
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    setFailure(null);
    getAiSpend()
      .then(setData, (error) => setFailure(isAiSpendMigrationMissing(error) ? 'migration' : 'failed'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const rows = useMemo(() => data?.rows || [], [data]);
  const totalSpent = rows.reduce((sum, row) => sum + Number(row.spentUsd || 0), 0);
  const redCount = rows.filter((row) => row.color === 'red').length;
  const orangeCount = rows.filter((row) => row.color === 'orange').length;

  const columns = [
    {
      id: 'client',
      header: 'Client',
      accessor: 'nom',
      sortable: true,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-fg">{row.nom}</p>
          <p className="truncate font-mono text-caption text-fg-subtle">{row.key}</p>
        </div>
      ),
    },
    {
      id: 'spent',
      header: 'Dépensé ce mois',
      accessor: 'spentUsd',
      align: 'right',
      sortable: true,
      cell: (row) => <span className="tabular-nums text-fg">{formatUsd(row.spentUsd)}</span>,
    },
    {
      id: 'budget',
      header: 'Seuil',
      accessor: 'budgetUsd',
      align: 'right',
      sortable: true,
      cell: (row) => <span className="tabular-nums text-fg-muted">{formatAiBudget(row.budgetUsd)}</span>,
    },
    {
      id: 'percent',
      header: '%',
      accessor: 'percent',
      align: 'right',
      sortable: true,
      cell: (row) => <span className="tabular-nums text-fg-muted">{formatPercent(row.percent)}</span>,
    },
    {
      id: 'color',
      header: 'Couleur',
      accessor: 'color',
      sortable: true,
      cell: (row) => <ColorBadge color={row.color} />,
    },
    {
      id: 'projected',
      header: 'Estimation fin de mois',
      accessor: 'projectedUsd',
      align: 'right',
      sortable: true,
      cell: (row) => <span className="tabular-nums text-fg-muted">{formatUsd(row.projectedUsd)}</span>,
    },
    {
      id: 'circuits',
      header: 'Répartition par circuit',
      cell: (row) => <CircuitBreakdown byCircuit={row.byCircuit} />,
    },
  ];

  const header = (
    <PageHeader
      title="Consommation IA"
      description={`Dépense IA du mois par client, en dollars${data ? ` (${aiMonthName(data.month)}, mois calendaire UTC)` : ''}. Le seuil se règle dans la fiche client : vide, pas de seuil (alerte de veille à 30 $) ; 0, IA coupée ce mois-ci. Cliquer sur une ligne pour le détail par jour et par utilisateur.`}
      actions={
        <IconButton variant="subtle" size="md" tooltip="Rafraîchir" onClick={load} disabled={loading}>
          <FiRefreshCw className={loading ? 'animate-spin' : ''} />
        </IconButton>
      }
    />
  );

  if (loading && !data) {
    return (
      <>
        {header}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </section>
      </>
    );
  }

  if (failure === 'migration') {
    return (
      <>
        {header}
        <EmptyState
          icon={<FiDatabase size={24} />}
          title={AI_SPEND_MIGRATION_MESSAGE}
          description="Les tables de suivi de la consommation IA n'existent pas encore sur la base centrale."
        />
      </>
    );
  }

  if (failure === 'failed' && !data) {
    return (
      <>
        {header}
        <EmptyState
          variant="danger"
          icon={<FiAlertTriangle size={24} />}
          title="Consommation indisponible"
          description="La consommation IA n'a pas pu être lue. Réessayer avec le bouton Rafraîchir."
        />
      </>
    );
  }

  return (
    <>
      {header}

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile
          icon={<FiActivity size={18} />}
          label="Dépensé ce mois"
          value={formatUsd(totalSpent)}
          tone="brand"
          hint={`tous clients, ${aiMonthName(data.month)}`}
        />
        <StatTile
          icon={<FiAlertOctagon size={18} />}
          label="Clients en rouge"
          value={redCount}
          tone="danger"
          hint="seuil atteint, IA bloquée sauf déblocage"
        />
        <StatTile
          icon={<FiAlertTriangle size={18} />}
          label="Clients en orange"
          value={orangeCount}
          tone="warning"
          hint="80 % du seuil atteint, ou débloqué"
        />
      </section>

      <ReconciliationCard days={data.reconciliation || []} />

      <Card padding="none" className="mt-5">
        <div className="border-b border-line px-4 py-3">
          <h3 className="text-body-sm font-semibold text-fg">Dépense par client</h3>
        </div>
        <DataTable
          columns={columns}
          data={rows}
          rowKey="key"
          onRowClick={setSelected}
          initialSort={{ columnId: 'spent', direction: 'desc' }}
          pageSize={25}
          empty={
            <EmptyState
              icon={<FiActivity size={24} />}
              title="Aucun client"
              description="Aucune consommation IA n'a encore été relevée."
            />
          }
        />
      </Card>

      <ClientDetail row={selected} onClose={() => setSelected(null)} />
    </>
  );
}

export default AiSpendDashboard;
